import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, SparklesIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type TabId = 'developer' | 'company' | 'recruiter' | 'jobpost';
const TABS: {
  id: TabId;
  label: string;
}[] = [{
  id: 'developer',
  label: 'I am a Developer'
}, {
  id: 'company',
  label: 'I represent a Company'
}, {
  id: 'recruiter',
  label: 'I am a Recruiter'
}, {
  id: 'jobpost',
  label: 'I want to post a Job'
}];
export function Pricing() {
  const [activeTab, setActiveTab] = useState<TabId>('developer');
  return <div className="w-full bg-background min-h-screen transition-colors duration-300" data-id="element-1552">
      {/* Hero */}
      <section className="pt-32 pb-12 relative overflow-hidden" data-id="element-1553">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5 pointer-events-none" data-id="element-1554" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1555">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1556">
            <h1 className="text-4xl md:text-5xl font-bold text-text-main mb-4 leading-tight" data-id="element-1557">
              Simple, transparent{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-1558">
                pricing
              </span>
            </h1>
            <p className="text-lg text-text-muted max-w-xl mx-auto" data-id="element-1559">
              Choose the plan that fits your needs. No hidden fees, cancel
              anytime.
            </p>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="pb-12" data-id="element-1560">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1561">
          <div className="flex justify-center" data-id="element-1562">
            <div className="inline-flex items-center p-1.5 bg-surface border border-border rounded-full shadow-sm overflow-x-auto max-w-full no-scrollbar" data-id="element-1563">
              {TABS.map(tab => {
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`relative px-6 py-2.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${isActive ? 'text-white' : 'text-text-muted hover:text-text-main hover:bg-background/50'}`} data-id="element-1564">
                    {isActive && <motion.div layoutId="activeTabIndicator" className="absolute inset-0 bg-gradient-to-r from-[#d73b3e] to-orange-500 rounded-full shadow-sm" initial={false} transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 30
                }} data-id="element-1565" />}
                    <span className="relative z-10" data-id="element-1566">{tab.label}</span>
                  </button>;
            })}
            </div>
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="pb-20 min-h-[600px]" data-id="element-1567">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1568">
          <AnimatePresence mode="wait" data-id="element-1569">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -15
          }} transition={{
            duration: 0.3,
            ease: 'easeOut'
          }} className="w-full" data-id="element-1570">
              {activeTab === 'developer' && <div className="max-w-4xl mx-auto" data-id="element-1571">
                  <div className="text-center mb-10" data-id="element-1572">
                    <h2 className="text-2xl font-bold text-text-main mb-2" data-id="element-1573">
                      Plans for Developers
                    </h2>
                    <p className="text-text-muted" data-id="element-1574">
                      Stand out and get hired as a certified developer
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8" data-id="element-1575">
                    {/* Developer (Free) Card */}
                    <div className="bg-surface border border-border rounded-2xl p-8 shadow-sm relative transition-colors duration-300" data-id="element-1576">
                      <div className="absolute -top-px left-0 right-0 h-1 bg-gradient-to-r from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-500 rounded-t-2xl" data-id="element-1577" />

                      <h3 className="text-xl font-bold text-text-main mb-1" data-id="element-1578">
                        Developer
                      </h3>
                      <p className="text-sm text-text-muted mb-6" data-id="element-1579">
                        For developers that want to showcase their certified
                        expertise
                      </p>

                      <div className="flex items-baseline gap-1 mb-8" data-id="element-1580">
                        <span className="text-4xl font-bold text-text-main" data-id="element-1581">
                          $0
                        </span>
                        <span className="text-text-muted text-sm" data-id="element-1582">/year</span>
                      </div>

                      <ul className="space-y-3 mb-8" data-id="element-1583">
                        <li className="flex items-start gap-3" data-id="element-1584">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1585" />
                          <span className="text-sm text-text-main" data-id="element-1586">
                            Create a profile
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1587">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1588" />
                          <span className="text-sm text-text-main" data-id="element-1589">
                            Listing on the CertifiedDevs database
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1590">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1591" />
                          <span className="text-sm text-text-main" data-id="element-1592">
                            Apply to jobs
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1593">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1594" />
                          <span className="text-sm text-text-main" data-id="element-1595">
                            Email support
                          </span>
                        </li>
                      </ul>

                      <button className="w-full py-3 px-6 bg-gradient-to-r from-[#d73b3e] to-orange-500 text-white rounded-lg font-semibold hover:from-[#c02f32] hover:to-orange-600 transition-all duration-300 shadow-md hover:shadow-lg" data-id="element-1596">
                        Get Started
                      </button>
                    </div>

                    {/* Developer Plus Card */}
                    <div className="bg-surface border-2 border-primary/20 rounded-2xl p-8 shadow-lg shadow-primary/5 relative transition-colors duration-300" data-id="element-1597">
                      <div className="absolute -top-px left-0 right-0 h-1 bg-gradient-to-r from-[#d73b3e] to-orange-500 rounded-t-2xl" data-id="element-1598" />

                      <h3 className="text-xl font-bold text-text-main mb-1" data-id="element-1599">
                        Developer Plus
                      </h3>
                      <p className="text-sm text-text-muted mb-6" data-id="element-1600">
                        For developers who want to stand out and boost their
                        career
                      </p>

                      <div className="flex items-baseline gap-1 mb-8" data-id="element-1601">
                        <span className="text-4xl font-bold text-text-main" data-id="element-1602">
                          $ 199.00
                        </span>
                        <span className="text-text-muted text-sm" data-id="element-1603">/year</span>
                      </div>

                      <ul className="space-y-3 mb-8" data-id="element-1604">
                        <li className="flex items-start gap-3" data-id="element-1605">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1606" />
                          <span className="text-sm text-text-main" data-id="element-1607">
                            Featured profile highlighting
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1608">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1609" />
                          <span className="text-sm text-text-main" data-id="element-1610">
                            Priority in customer pages / AI
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1611">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1612" />
                          <span className="text-sm text-text-main" data-id="element-1613">
                            Site-wide feature & AI
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1614">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1615" />
                          <span className="text-sm text-text-main" data-id="element-1616">
                            LinkedIn promotion
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1617">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1618" />
                          <span className="text-sm text-text-main" data-id="element-1619">
                            24/7 priority email assistance
                          </span>
                        </li>
                      </ul>

                      <button className="w-full py-3 px-6 bg-gradient-to-r from-[#d73b3e] to-orange-500 text-white rounded-lg font-semibold hover:from-[#c02f32] hover:to-orange-600 transition-all duration-300 shadow-md hover:shadow-lg" data-id="element-1620">
                        Get Started
                      </button>
                    </div>
                  </div>
                </div>}

              {activeTab === 'company' && <div className="max-w-4xl mx-auto" data-id="element-1621">
                  <div className="text-center mb-10" data-id="element-1622">
                    <h2 className="text-2xl font-bold text-text-main mb-2" data-id="element-1623">
                      Plans for Companies
                    </h2>
                    <p className="text-text-muted" data-id="element-1624">
                      Find and hire certified developers
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8 mb-10" data-id="element-1625">
                    {/* Company */}
                    <div className="bg-surface border border-border rounded-2xl p-8 shadow-sm relative transition-colors duration-300" data-id="element-1626">
                      <div className="absolute -top-px left-0 right-0 h-1 bg-gradient-to-r from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-500 rounded-t-2xl" data-id="element-1627" />

                      <h3 className="text-xl font-bold text-text-main mb-1" data-id="element-1628">
                        Company
                      </h3>
                      <p className="text-sm text-text-muted mb-6" data-id="element-1629">
                        For growing companies
                      </p>

                      <div className="flex items-baseline gap-1 mb-8" data-id="element-1630">
                        <span className="text-4xl font-bold text-text-main" data-id="element-1631">
                          $ 599.00
                        </span>
                        <span className="text-text-muted text-sm" data-id="element-1632">/year</span>
                      </div>

                      <ul className="space-y-3 mb-8" data-id="element-1633">
                        <li className="flex items-start gap-3" data-id="element-1634">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1635" />
                          <span className="text-sm text-text-main" data-id="element-1636">
                            Certified talent badge
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1637">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1638" />
                          <span className="text-sm text-text-main" data-id="element-1639">
                            Contact up to 5 freelancers
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1640">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1641" />
                          <span className="text-sm text-text-main" data-id="element-1642">
                            Basic team building features
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1643">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1644" />
                          <span className="text-sm text-text-main" data-id="element-1645">
                            Company entry in the dashboard
                          </span>
                        </li>
                      </ul>

                      <button className="w-full py-3 px-6 bg-gradient-to-r from-[#d73b3e] to-orange-500 text-white rounded-lg font-semibold hover:from-[#c02f32] hover:to-orange-600 transition-all duration-300 shadow-md hover:shadow-lg" data-id="element-1646">
                        Get Started
                      </button>
                    </div>

                    {/* Company Plus */}
                    <div className="bg-surface border-2 border-primary/20 rounded-2xl p-8 shadow-lg shadow-primary/5 relative transition-colors duration-300" data-id="element-1647">
                      <div className="absolute -top-px left-0 right-0 h-1 bg-gradient-to-r from-[#d73b3e] to-orange-500 rounded-t-2xl" data-id="element-1648" />

                      <h3 className="text-xl font-bold text-text-main mb-1" data-id="element-1649">
                        Company Plus
                      </h3>
                      <p className="text-sm text-text-muted mb-6" data-id="element-1650">
                        For scaling teams
                      </p>

                      <div className="flex items-baseline gap-1 mb-8" data-id="element-1651">
                        <span className="text-4xl font-bold text-text-main" data-id="element-1652">
                          $ 2,499.00
                        </span>
                        <span className="text-text-muted text-sm" data-id="element-1653">/year</span>
                      </div>

                      <ul className="space-y-3 mb-8" data-id="element-1654">
                        <li className="flex items-start gap-3" data-id="element-1655">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1656" />
                          <span className="text-sm text-text-main" data-id="element-1657">
                            All Company features
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1658">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1659" />
                          <span className="text-sm text-text-main" data-id="element-1660">
                            Direct messaging to all freelancers
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1661">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1662" />
                          <span className="text-sm text-text-main" data-id="element-1663">
                            Advanced team management
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1664">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1665" />
                          <span className="text-sm text-text-main" data-id="element-1666">
                            Priority job listing placement
                          </span>
                        </li>
                      </ul>

                      <button className="w-full py-3 px-6 bg-gradient-to-r from-[#d73b3e] to-orange-500 text-white rounded-lg font-semibold hover:from-[#c02f32] hover:to-orange-600 transition-all duration-300 shadow-md hover:shadow-lg" data-id="element-1667">
                        Get Started
                      </button>
                    </div>
                  </div>
                </div>}

              {activeTab === 'recruiter' && <div className="max-w-4xl mx-auto" data-id="element-1668">
                  <div className="text-center mb-10" data-id="element-1669">
                    <h2 className="text-2xl font-bold text-text-main mb-2" data-id="element-1670">
                      Plans for Recruiters
                    </h2>
                    <p className="text-text-muted" data-id="element-1671">
                      Access top certified talent for your clients
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8" data-id="element-1672">
                    {/* Recruiter */}
                    <div className="bg-surface border border-border rounded-2xl p-8 shadow-sm relative transition-colors duration-300" data-id="element-1673">
                      <div className="absolute -top-px left-0 right-0 h-1 bg-gradient-to-r from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-500 rounded-t-2xl" data-id="element-1674" />

                      <h3 className="text-xl font-bold text-text-main mb-1" data-id="element-1675">
                        Recruiter
                      </h3>
                      <p className="text-sm text-text-muted mb-6" data-id="element-1676">
                        For independent recruiters
                      </p>

                      <div className="flex items-baseline gap-1 mb-8" data-id="element-1677">
                        <span className="text-4xl font-bold text-text-main" data-id="element-1678">
                          $ 1,599.00
                        </span>
                        <span className="text-text-muted text-sm" data-id="element-1679">/year</span>
                      </div>

                      <ul className="space-y-3 mb-8" data-id="element-1680">
                        <li className="flex items-start gap-3" data-id="element-1681">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1682" />
                          <span className="text-sm text-text-main" data-id="element-1683">
                            Full access to all freelancers
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1684">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1685" />
                          <span className="text-sm text-text-main" data-id="element-1686">
                            Unlimited contact & introductions
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1687">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1688" />
                          <span className="text-sm text-text-main" data-id="element-1689">
                            Advanced search and filtering
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1690">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1691" />
                          <span className="text-sm text-text-main" data-id="element-1692">
                            Candidate tracking tools
                          </span>
                        </li>
                      </ul>

                      <button className="w-full py-3 px-6 bg-gradient-to-r from-[#d73b3e] to-orange-500 text-white rounded-lg font-semibold hover:from-[#c02f32] hover:to-orange-600 transition-all duration-300 shadow-md hover:shadow-lg" data-id="element-1693">
                        Get Started
                      </button>
                    </div>

                    {/* Recruiter Plus */}
                    <div className="bg-surface border-2 border-primary/20 rounded-2xl p-8 shadow-lg shadow-primary/5 relative transition-colors duration-300" data-id="element-1694">
                      <div className="absolute -top-px left-0 right-0 h-1 bg-gradient-to-r from-[#d73b3e] to-orange-500 rounded-t-2xl" data-id="element-1695" />

                      <h3 className="text-xl font-bold text-text-main mb-1" data-id="element-1696">
                        Recruiter Plus
                      </h3>
                      <p className="text-sm text-text-muted mb-6" data-id="element-1697">
                        For recruitment agencies
                      </p>

                      <div className="flex items-baseline gap-1 mb-8" data-id="element-1698">
                        <span className="text-4xl font-bold text-text-main" data-id="element-1699">
                          $ 2,499.00
                        </span>
                        <span className="text-text-muted text-sm" data-id="element-1700">/year</span>
                      </div>

                      <ul className="space-y-3 mb-8" data-id="element-1701">
                        <li className="flex items-start gap-3" data-id="element-1702">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1703" />
                          <span className="text-sm text-text-main" data-id="element-1704">
                            All Recruiter features
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1705">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1706" />
                          <span className="text-sm text-text-main" data-id="element-1707">
                            Priority access to new freelancers
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1708">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1709" />
                          <span className="text-sm text-text-main" data-id="element-1710">
                            Advanced analytics and reports
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1711">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1712" />
                          <span className="text-sm text-text-main" data-id="element-1713">
                            Custom integrations
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1714">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1715" />
                          <span className="text-sm text-text-main" data-id="element-1716">
                            Priority 24/7 support
                          </span>
                        </li>
                      </ul>

                      <button className="w-full py-3 px-6 bg-gradient-to-r from-[#d73b3e] to-orange-500 text-white rounded-lg font-semibold hover:from-[#c02f32] hover:to-orange-600 transition-all duration-300 shadow-md hover:shadow-lg" data-id="element-1717">
                        Get Started
                      </button>
                    </div>
                  </div>
                </div>}

              {activeTab === 'jobpost' && <div className="max-w-4xl mx-auto" data-id="element-1718">
                  <div className="text-center mb-10" data-id="element-1719">
                    <h2 className="text-2xl font-bold text-text-main mb-2" data-id="element-1720">
                      Pay-As-You-Go Job Posts
                    </h2>
                    <p className="text-text-muted" data-id="element-1721">
                      No subscription required. Pay only when you need to post a
                      job.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8" data-id="element-1722">
                    {/* Standard Job Post */}
                    <div className="bg-surface border border-border rounded-2xl p-8 shadow-sm relative transition-colors duration-300" data-id="element-1723">
                      <h3 className="text-xl font-bold text-text-main mb-1" data-id="element-1724">
                        Standard Job Post
                      </h3>
                      <p className="text-sm text-text-muted mb-6" data-id="element-1725">
                        Find the right matching results
                      </p>

                      <div className="flex items-baseline gap-1 mb-2" data-id="element-1726">
                        <span className="text-4xl font-bold text-text-main" data-id="element-1727">
                          $99
                        </span>
                        <span className="text-text-muted text-sm" data-id="element-1728">
                          / 30 days
                        </span>
                      </div>
                      <p className="text-xs text-text-muted mb-8" data-id="element-1729">
                        Can be extended for 30 more days
                      </p>

                      <ul className="space-y-3 mb-8" data-id="element-1730">
                        <li className="flex items-start gap-3" data-id="element-1731">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1732" />
                          <span className="text-sm text-text-main" data-id="element-1733">
                            Basic targeted & local search distribution
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1734">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1735" />
                          <span className="text-sm text-text-main" data-id="element-1736">
                            Up to 5 free re-posts in 30 days
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1737">
                          <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1738" />
                          <span className="text-sm text-text-main" data-id="element-1739">
                            Standard placement
                          </span>
                        </li>
                      </ul>

                      <button className="w-full py-3 px-6 bg-gradient-to-r from-[#d73b3e] to-orange-500 text-white rounded-lg font-semibold hover:from-[#c02f32] hover:to-orange-600 transition-all duration-300 shadow-md hover:shadow-lg" data-id="element-1740">
                        Post Standard Job
                      </button>
                    </div>

                    {/* Featured Job Post */}
                    <div className="bg-surface border-2 border-primary/20 rounded-2xl p-8 shadow-lg shadow-primary/5 relative transition-colors duration-300" data-id="element-1741">
                      <div className="absolute -top-px left-0 right-0 h-1 bg-gradient-to-r from-[#d73b3e] to-orange-500 rounded-t-2xl" data-id="element-1742" />

                      {/* Promoted Badge */}
                      <div className="absolute top-6 right-6" data-id="element-1743">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-[#d73b3e] to-orange-500 text-white text-xs font-bold rounded-full shadow-md" data-id="element-1744">
                          <SparklesIcon className="w-3.5 h-3.5" data-id="element-1745" />
                          PROMOTED
                        </span>
                      </div>

                      <h3 className="text-xl font-bold text-text-main mb-1" data-id="element-1746">
                        Featured Job Post
                      </h3>
                      <p className="text-sm text-text-muted mb-6" data-id="element-1747">
                        Maximum visibility and reach
                      </p>

                      <div className="flex items-baseline gap-1 mb-2" data-id="element-1748">
                        <span className="text-4xl font-bold text-text-main" data-id="element-1749">
                          $249
                        </span>
                        <span className="text-text-muted text-sm" data-id="element-1750">
                          / 30 days
                        </span>
                      </div>
                      <p className="text-xs text-text-muted mb-8" data-id="element-1751">
                        Can be extended for 30 more days
                      </p>

                      <ul className="space-y-3 mb-8" data-id="element-1752">
                        <li className="flex items-start gap-3" data-id="element-1753">
                          <CheckIcon className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" data-id="element-1754" />
                          <span className="text-sm text-text-main" data-id="element-1755">
                            Everything in Standard
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1756">
                          <CheckIcon className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" data-id="element-1757" />
                          <span className="text-sm text-text-main" data-id="element-1758">
                            Featured listing on homepage
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1759">
                          <CheckIcon className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" data-id="element-1760" />
                          <span className="text-sm text-text-main" data-id="element-1761">
                            Priority placement in search listings
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1762">
                          <CheckIcon className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" data-id="element-1763" />
                          <span className="text-sm text-text-main" data-id="element-1764">
                            Social media promotion
                          </span>
                        </li>
                        <li className="flex items-start gap-3" data-id="element-1765">
                          <CheckIcon className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" data-id="element-1766" />
                          <span className="text-sm text-text-main" data-id="element-1767">
                            2x more visibility
                          </span>
                        </li>
                      </ul>

                      <button className="w-full py-3 px-6 bg-gradient-to-r from-[#d73b3e] to-orange-500 text-white rounded-lg font-semibold hover:from-[#c02f32] hover:to-orange-600 transition-all duration-300 shadow-md hover:shadow-lg" data-id="element-1768">
                        Post Featured Job
                      </button>
                    </div>
                  </div>
                </div>}

              {/* Trust line - shown at the bottom of every tab */}
              <p className="text-center text-sm text-text-muted mt-12" data-id="element-1769">
                Trusted by 3,000+ certified developers and 500+ companies
                worldwide
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-black text-white" data-id="element-1770">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1771">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1772">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight" data-id="element-1773">
              Not sure which plan is right?{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-1774">
                Let's talk.
              </span>
            </h2>
            <p className="text-xl text-gray-300 mb-10" data-id="element-1775">
              Our team can help you find the perfect plan for your needs.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1776">
              <a href="mailto:hello@certifieddevs.com" className="px-8 py-4 bg-primary text-white rounded-full font-semibold text-lg hover:bg-primary-hover transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-1777">
                Contact Sales
              </a>
              <Link to="/developers" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all duration-300 border-2 border-white/20" data-id="element-1778">
                Browse Developers
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>;
}
export default Pricing;